import { Collapse } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { csvDelimiterEnum } from '../../utils/enums';
import CustomDateInput from '../CustomDateInput';
import CustomTextInput from '../CustomTextInput';
import DropDown, { OptionType } from '../DropDown';
import Icon from '../shared/Icon';
import ModalBody, { ModalProps } from './ModalBody';

interface ExportCsvModalProps extends ModalProps {
    onExport: (startDate: string, endDate: string, fileName?: string, delimiter?: string) => void;
    initDate: Date;
    children?: React.ReactNode;
    subTitle?: string;
}

const ExportCsvModal: React.FC<ExportCsvModalProps> = ({ onExport, initDate, children, handleClose, open, subTitle }) => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState(initDate);
    const [endDate, setEndDate] = useState(initDate);
    const [delimiter, setDelimiter] = useState<OptionType | null>({ label: csvDelimiterEnum.SEMICOLON, value: csvDelimiterEnum.SEMICOLON });
    const [fileName, setFileName] = useState('');
    const [showCsvConfig, setShowCsvConfig] = useState(false);

    return (
        <ModalBody
            title={t('downloadCsv')}
            subTitle={subTitle}
            open={open}
            handleClose={() => handleClose()}
            okText={t('validate')}
            handleOk={() => onExport(startDate.toISOString(), endDate.toISOString(), fileName.trim(), delimiter?.value || '')}
        >
            <CustomDateInput
                label={t('startDate')}
                onChange={(value) => (value ? setStartDate(new Date(value?.toISOString())) : null)}
                defaultDate={startDate}
                required
                maxDate={endDate}
            />
            <CustomDateInput
                label={t('endDate')}
                onChange={(value) => (value ? setEndDate(new Date(value?.toISOString())) : null)}
                defaultDate={endDate}
                required
                minDate={startDate}
            />
            {children}
            <div className="w-100 pad-15px">
                <div className="h-1px bg-lightgray w-100" />
            </div>

            <div className="fx-r w-300px fx-justify-sb pad-l-15px pad-r-15px" onClick={() => setShowCsvConfig(!showCsvConfig)}>
                <div>{`${capitalizeFirstLetter(t('advancedOptions'))} (${t('optional')})`}</div>
                <Icon name={showCsvConfig ? 'keyboardArrowDownSharp' : 'keyboardArrowRightSharp'}  />
            </div>
            <Collapse in={showCsvConfig}>
                <div className="fx-c gap-5px">
                    <DropDown
                        items={Object.values(csvDelimiterEnum).map((value) => ({ value: value, label: value }))}
                        handleChange={(event, value) => setDelimiter(value)}
                        inputLabel={capitalizeFirstLetter(t('delimiter'))}
                        selectedItem={delimiter}
                    />
                    <CustomTextInput
                        label={capitalizeFirstLetter(t('fileName'))}
                        value={fileName}
                        onChange={(text) => setFileName(text)}
                        endAdornment={<div>.csv</div>}
                    />
                </div>
            </Collapse>
        </ModalBody>
    );
};

export default ExportCsvModal;
